import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal, Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { type MovableAssetItemFormType, getMovableAssetItemFormSchema } from "Forms/Schemas/Assets/movableAssetItemFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetAssetsOverviewQueryKey,
  getListMovableAssetItemsQueryKey,
  useCreateMovableAssetItem,
  useDeleteAssetItem,
  useUpdateMovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type MovableAssetItem, MovableAssetItemMovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { MOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly movableAsset?: MovableAssetItem;
  readonly open: boolean;
};

export const MovableAssetItemDrawer: FC<Props> = ({ onClose, movableAsset, open }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const { t } = useResource();
  const queryClient = useQueryClient();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListMovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create, isPending: isCreating } = useCreateMovableAssetItem();
  const { mutate: update, isPending: isUpdating } = useUpdateMovableAssetItem();
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteAssetItem();

  const isMutating = isCreating || isUpdating || isDeleting;

  const movableAssets: Option[] = (Object.keys(MovableAssetItemMovableSubType) as Array<MovableAssetItemMovableSubType>).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType[MOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const methods = useForm<MovableAssetItemFormType>({
    resolver: yupResolver(getMovableAssetItemFormSchema(t)),
    mode: "onBlur",
  });

  const { control, handleSubmit, reset, watch } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        movableAssetType: movableAsset?.movableSubType,
        name: movableAsset?.name,
        value: movableAsset?.amount.value,
        note: movableAsset?.note ?? undefined,
        vehicleIdentificationNumber: movableAsset?.vehicleIdentificationNumber ?? undefined,
        vehicleLicensePlate: movableAsset?.vehicleLicensePlate ?? undefined,
        vehiclePrice: movableAsset?.vehiclePrice?.value,
        companyIdentificationNumber: movableAsset?.companyIdentificationNumber ?? undefined,
        businessSector: movableAsset?.businessSector ?? undefined,
      };
      reset(defaultValues);
      return;
    }

    reset({});
  }, [movableAsset, open, reset]);

  const movableAssetType = watch("movableAssetType");
  const isVehicle = movableAssetType === MovableAssetItemMovableSubType.vehicle;
  const isCompany = movableAssetType === MovableAssetItemMovableSubType.company;

  const onSubmit = (data: MovableAssetItemFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        movableSubType: data.movableAssetType as MovableAssetItemMovableSubType,
        isCoveredByInsurance: !!movableAsset?.isCoveredByInsurance,
        isUnderFinancing: !!movableAsset?.isUnderFinancing,
        isUsableForGoals: !!movableAsset?.isUsableForGoals,
        vehicleIdentificationNumber: isVehicle ? data.vehicleIdentificationNumber || undefined : undefined,
        vehicleLicensePlate: isVehicle ? data.vehicleLicensePlate || undefined : undefined,
        vehiclePrice:
          isVehicle && data.vehiclePrice
            ? { value: data.vehiclePrice, currencyCode: "CZK", frequency: AmountFrequency.one_time }
            : undefined,
        companyIdentificationNumber: isCompany ? data.companyIdentificationNumber || undefined : undefined,
        businessSector: isCompany ? data.businessSector || undefined : undefined,
      };

      if (!movableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: movableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: movableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Movable.Deleted));
            invalidate();
            setDeleteModalOpen(false);
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Drawer
          open={open}
          onClose={onClose}
          closeLabel={t(Resources.Common.Close)}
          title={movableAsset ? t(Resources.AnalysisDetail.Assets.Movable.Edit) : t(Resources.AnalysisDetail.Assets.Movable.Add)}
          onSubmit={handleSubmit(onSubmit)}
          startAction={
            !!movableAsset && (
              <Button variant="text" color="error" disabled={readOnly || isMutating} onClick={() => setDeleteModalOpen(true)}>
                {t(Resources.Common.Delete)}
              </Button>
            )
          }
          primaryAction={
            <Button variant="contained" disabled={readOnly || isMutating} type="submit">
              {movableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
            </Button>
          }
        >
          <Box py={Spacing.XXL}>
            <Grid container spacing={Spacing.XL}>
              <Grid size={4}>
                <FormSelect
                  control={control}
                  name="movableAssetType"
                  label={t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType.Title)}
                  required
                  options={toSelectOptions(movableAssets)}
                  placeholder={t(Resources.Common.SelectPlaceholder)}
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid size={8}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Name)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <FormNumberField
                  control={control}
                  name="value"
                  label={t(Resources.AnalysisDetail.Assets.Value)}
                  required
                  scale={0}
                  min={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled
                  placeholder="0"
                  fullWidth
                />
              </Grid>

              {isVehicle && (
                <>
                  <Grid size={2}>
                    <FormTextField
                      control={control}
                      name="vehicleLicensePlate"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleLicencePlate)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={5}>
                    <FormTextField
                      control={control}
                      name="vehicleIdentificationNumber"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VIN)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={5}>
                    <FormNumberField
                      control={control}
                      name="vehiclePrice"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehiclePrice)}
                      valueType="text"
                      scale={0}
                      min={0}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              {isCompany && (
                <>
                  <Grid size={12}>
                    <FormTextField
                      control={control}
                      name="companyIdentificationNumber"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.CompanyIdentificationNumber)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={12}>
                    <FormTextField
                      control={control}
                      name="businessSector"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.BusinessSector)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.Common.Note)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Drawer>
      </FormProvider>
      <DialogModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        closeLabel={t(Resources.Common.Close)}
        title={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetTitle)}
        subtitle={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetText)}
        primaryAction={
          <Button variant="contained" disabled={readOnly || isMutating} onClick={onDelete}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </>
  );
};
